import { useMemo, useState } from "react";

import type { MasterControlOrganization } from "@/api/executive";
import { syncBillingSeatLimit, updateBillingSeatLimit } from "@/api/billing";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from "@/components/ui/drawer";
import { useToast } from "@/components/ToastProvider";
import { planLabel } from "@/lib/plans";

function formatDate(value?: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
}

export type MCOrgsTableProps = {
  organizations: MasterControlOrganization[];
  loading?: boolean;
};

export function MCOrgsTable({ organizations, loading }: MCOrgsTableProps) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<MasterControlOrganization | null>(null);
  const [seatLimitValue, setSeatLimitValue] = useState<number>(1);
  const [savingLimit, setSavingLimit] = useState(false);
  const [syncingLimit, setSyncingLimit] = useState(false);
  const { push } = useToast();

  const sorted = useMemo(() => {
    return [...organizations].sort((a, b) => (b.member_count ?? 0) - (a.member_count ?? 0));
  }, [organizations]);

  return (
    <Card className="rounded-2xl border border-border bg-background/60 shadow-none">
      <CardHeader className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <CardTitle className="text-lg font-semibold text-foreground">Organizations</CardTitle>
          <p className="text-sm text-muted-foreground">Ownership, plan, and cadence.</p>
        </div>
        <div className="text-xs text-muted-foreground">{loading ? "Refreshing…" : `${organizations.length} orgs`}</div>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Members</TableHead>
              <TableHead>Seat limit</TableHead>
              <TableHead>Plan</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Last activity</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((org) => (
              <TableRow key={org.id}>
                <TableCell>
                  <div className="text-sm font-semibold text-foreground">{org.name ?? org.id}</div>
                  <p className="text-xs text-muted-foreground">{org.id}</p>
                </TableCell>
                <TableCell className="text-sm text-foreground/80">{org.member_count ?? "—"}</TableCell>
                <TableCell className="text-sm text-foreground/80">{org.seat_limit ?? "—"}</TableCell>
                <TableCell className="text-sm text-foreground/80">{planLabel(org.subscription_tier)}</TableCell>
                <TableCell className="text-sm text-foreground/80">{formatDate(org.created_at)}</TableCell>
                <TableCell className="text-sm text-foreground/80">{formatDate(org.last_activity_at)}</TableCell>
                <TableCell className="text-right">
                  <Drawer open={open && selected?.id === org.id} onOpenChange={(next) => (next ? (setSelected(org), setOpen(true)) : setOpen(false))}>
                    <DrawerTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelected(org);
                          setOpen(true);
                          const fallback = typeof org.seat_limit === "number" ? org.seat_limit : org.member_count ?? 1;
                          setSeatLimitValue(Math.max(1, fallback || 1));
                        }}
                      >
                        Open org
                      </Button>
                    </DrawerTrigger>
                    <DrawerContent className="bg-background text-foreground">
                      <DrawerHeader>
                        <DrawerTitle>{selected?.name ?? "Organization"}</DrawerTitle>
                      </DrawerHeader>
                      <div className="space-y-4 border-t border-border px-6 py-4 text-sm text-foreground/80">
                        <dl className="grid gap-4 md:grid-cols-2">
                          <div>
                            <dt className="text-xs uppercase tracking-wide text-muted-foreground">Org ID</dt>
                            <dd>{selected?.id}</dd>
                          </div>
                          <div>
                            <dt className="text-xs uppercase tracking-wide text-muted-foreground">Members</dt>
                            <dd>{selected?.member_count ?? "—"}</dd>
                          </div>
                          <div>
                            <dt className="text-xs uppercase tracking-wide text-muted-foreground">Seat limit</dt>
                            <dd>{selected?.seat_limit ?? "—"}</dd>
                          </div>
                          <div>
                            <dt className="text-xs uppercase tracking-wide text-muted-foreground">Plan</dt>
                            <dd>{planLabel(selected?.subscription_tier)}</dd>
                          </div>
                          <div>
                            <dt className="text-xs uppercase tracking-wide text-muted-foreground">Subscription</dt>
                            <dd>{selected?.stripe_subscription_status ?? "—"}</dd>
                          </div>
                          <div>
                            <dt className="text-xs uppercase tracking-wide text-muted-foreground">Status</dt>
                            <dd>{selected?.status ?? "—"}</dd>
                          </div>
                          <div>
                            <dt className="text-xs uppercase tracking-wide text-muted-foreground">Created</dt>
                            <dd>{formatDate(selected?.created_at)}</dd>
                          </div>
                          <div>
                            <dt className="text-xs uppercase tracking-wide text-muted-foreground">Last activity</dt>
                            <dd>{formatDate(selected?.last_activity_at)}</dd>
                          </div>
                        </dl>
                        <div className="rounded-2xl border border-border bg-background/40 p-4">
                          <p className="text-xs uppercase tracking-wide text-muted-foreground">Emergency seat limit</p>
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <Input
                              type="number"
                              min={1}
                              value={seatLimitValue}
                              onChange={(event) => setSeatLimitValue(Number(event.target.value) || 1)}
                              className="w-32"
                            />
                            <Button
                              size="sm"
                              onClick={async () => {
                                if (!selected) return;
                                setSavingLimit(true);
                                try {
                                  const payload = await updateBillingSeatLimit({
                                    org_id: selected.id,
                                    seat_limit: seatLimitValue,
                                  });
                                  setSelected((prev) =>
                                    prev ? { ...prev, seat_limit: payload.seat_limit } : prev,
                                  );
                                  push({ title: "Seat limit updated", message: `Set to ${payload.seat_limit}.` });
                                } catch (err) {
                                  push({ title: "Seat limit failed", message: err instanceof Error ? err.message : "Unable to update seat limit." });
                                } finally {
                                  setSavingLimit(false);
                                }
                              }}
                              disabled={savingLimit}
                            >
                              {savingLimit ? "Saving…" : "Update limit"}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={async () => {
                                if (!selected) return;
                                setSyncingLimit(true);
                                try {
                                  const payload = await syncBillingSeatLimit({ org_id: selected.id });
                                  if (typeof payload.seat_limit === "number") {
                                    setSeatLimitValue(payload.seat_limit);
                                    setSelected((prev) =>
                                      prev ? { ...prev, seat_limit: payload.seat_limit, stripe_subscription_status: payload.status ?? prev.stripe_subscription_status } : prev,
                                    );
                                  }
                                  push({ title: "Seat limit synced", message: "Stripe subscription refreshed." });
                                } catch (err) {
                                  push({ title: "Sync failed", message: err instanceof Error ? err.message : "Unable to sync from Stripe." });
                                } finally {
                                  setSyncingLimit(false);
                                }
                              }}
                              disabled={syncingLimit}
                            >
                              {syncingLimit ? "Syncing…" : "Sync from Stripe"}
                            </Button>
                          </div>
                          <p className="mt-2 text-xs text-muted-foreground">
                            Use this override only for emergency seat adjustments.
                          </p>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-wide text-muted-foreground">Raw payload</p>
                          <pre className="mt-2 max-h-64 overflow-auto rounded-xl border border-border bg-background p-4 text-xs text-foreground/70">
                            {JSON.stringify(selected ?? {}, null, 2)}
                          </pre>
                        </div>
                      </div>
                    </DrawerContent>
                  </Drawer>
                </TableCell>
              </TableRow>
            ))}
            {!loading && sorted.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-6 text-center text-sm text-muted-foreground">
                  No organizations found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export default MCOrgsTable;
